import { useState, useEffect, useMemo } from 'react';
import { useContracts } from '../../../hooks/useContracts';
import { useGroups } from '../../../hooks/useGroups';
import { ITEMS_PER_PAGE } from '../constants/contractsConstants';

/**
 * Hook para manejar la lógica del diálogo de edición de lista de contratos
 * Este hook se encarga de obtener y paginar los contratos filtrados
 */
export const useContractEditList = ({ contractFilters = {}, open, save, setSave }) => {
  const [filterInputs, setFilterInputs] = useState({
    groupUuid: '',
    dni: '',
    contractNumber: '',
    name: '',
  });
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(ITEMS_PER_PAGE);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);

  const { data: groups = [] } = useGroups();

  const { data: contracts = [], isLoading } = useContracts(
    contractFilters,
    contractFilters.archived || false
  );

  useEffect(() => {
    setPage(1);
    if (open) {
      setFilterInputs({
        groupUuid: '',
        dni: '',
        contractNumber: '',
        name: '',
      });
    }
  }, [contractFilters, open]);

  const filteredContracts = useMemo(() => {
    let filtered = contracts;

    if (filterInputs.groupUuid && filterInputs.groupUuid !== 0) {
      filtered = filtered.filter((contract) => contract.uuid_group === filterInputs.groupUuid);
    }

    if (filterInputs.dni.trim()) {
      const searchDni = filterInputs.dni.toLowerCase();
      filtered = filtered.filter((contract) => {
        if (!contract.subscribers || contract.subscribers.length === 0) return false;
        return contract.subscribers.some((subscriber) =>
          subscriber.subscriber?.dni?.toLowerCase().includes(searchDni)
        );
      });
    }

    if (filterInputs.contractNumber.trim()) {
      const searchNumber = filterInputs.contractNumber.toLowerCase();
      filtered = filtered.filter((contract) =>
        contract.contract_number?.toLowerCase().includes(searchNumber)
      );
    }

    if (filterInputs.name.trim()) {
      const searchName = filterInputs.name.toLowerCase();
      filtered = filtered.filter((contract) => {
        if (!contract.subscribers || contract.subscribers.length === 0) return false;
        return contract.subscribers.some(
          (subscriber) =>
            subscriber.subscriber?.first_name?.toLowerCase().includes(searchName) ||
            subscriber.subscriber?.last_name?.toLowerCase().includes(searchName)
        );
      });
    }

    return filtered;
  }, [contracts, filterInputs]);

  const displayedContracts = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredContracts.slice(startIndex, endIndex);
  }, [filteredContracts, page, rowsPerPage]);

  const totalPages = Math.max(1, Math.ceil(filteredContracts.length / rowsPerPage));
  const totalCount = filteredContracts.length;

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const handleFilterChange = (name, value) => {
    setFilterInputs((prev) => ({
      ...prev,
      [name]: value,
    }));
    setPage(1);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    const value = parseInt(event.target.value, 10);
    if (!Number.isNaN(value)) {
      setRowsPerPage(value);
      setPage(1);
    }
  };

  const handleEdit = (contract) => {
    setSelectedContract(contract);
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedContract(null);
  };

  const handleSaveContract = () => {
    setSave(!save);
    setEditDialogOpen(false);
    setSelectedContract(null);
  };

  return {
    displayedContracts,
    isLoading,

    filterInputs,
    page,
    totalPages,
    totalCount,
    rowsPerPage,

    groups,

    handleFilterChange,
    handlePageChange,
    handleRowsPerPageChange,
    handleEdit,

    editDialogOpen,
    selectedContract,
    handleCloseEditDialog,
    handleSaveContract,
  };
};
